import { useEffect, useState } from "react";
import axios from "axios";
import { LogIn, LogOut, RefreshCw, Loader2, Users, Mail, Search, Lock, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function downloadCsv(filename, rows, columns) {
  if (!rows.length) return;
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n\r]/.test(s) ? `"${s}"` : s;
  };
  const header = columns.map((c) => escape(c.label)).join(",");
  const body = rows
    .map((r) => columns.map((c) => escape(c.get ? c.get(r) : r[c.key])).join(","))
    .join("\n");
  const blob = new Blob([`${header}\n${body}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const APP_COLUMNS = [
  { key: "full_name", label: "Full Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "qualification", label: "Qualification" },
  { key: "domain", label: "Preferred Domain" },
  { key: "college", label: "College" },
  { key: "message", label: "Message" },
  { key: "created_at", label: "Received At" },
];

const CONTACT_COLUMNS = [
  { key: "full_name", label: "Full Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "subject", label: "Subject" },
  { key: "message", label: "Message" },
  { key: "created_at", label: "Received At" },
];

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("sconti_admin_token") || "");
  const [tab, setTab] = useState("applications");
  const [applications, setApplications] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const loadData = async (tk = token) => {
    if (!tk) return;
    setLoading(true);
    setError("");
    try {
      const [appsRes, ctcRes] = await Promise.all([
        axios.get(`${API}/applications`, { headers: { Authorization: `Bearer ${tk}` } }),
        axios.get(`${API}/contact`, { headers: { Authorization: `Bearer ${tk}` } }),
      ]);
      setApplications(appsRes.data || []);
      setContacts(ctcRes.data || []);
    } catch (err) {
      if (err.response?.status === 401) {
        setToken("");
        localStorage.removeItem("sconti_admin_token");
        setError("Session expired. Please sign in again.");
      } else {
        setError(err.response?.data?.detail || "Failed to load data.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadData(token);
  }, []);

  const handleLogin = async (username, password) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      const tk = res.data.token;
      localStorage.setItem("sconti_admin_token", tk);
      setToken(tk);
      await loadData(tk);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("sconti_admin_token");
    setToken("");
    setApplications([]);
    setContacts([]);
  };

  if (!token) {
    return <LoginView onLogin={handleLogin} loading={loading} error={error} />;
  }

  const filteredApps = filter(applications, query, ["full_name", "email", "phone", "domain", "college"]);
  const filteredContacts = filter(contacts, query, ["full_name", "email", "subject", "message"]);

  return (
    <div className="min-h-screen bg-neutral-50 pt-24 md:pt-28">
      <div className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="flex items-start md:items-center justify-between flex-col md:flex-row gap-6 mb-10">
          <div>
            <span className="text-xs font-mono uppercase tracking-[0.24em] text-neutral-500">Admin Dashboard</span>
            <h1 className="mt-2 font-serif text-4xl md:text-5xl tracking-tight leading-tight">
              Submissions overview
            </h1>
            <p className="mt-2 text-neutral-600 text-sm">
              {applications.length} applications · {contacts.length} contact messages
            </p>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto flex-wrap">
            <div className="relative flex-1 md:flex-none">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" strokeWidth={1.75} />
              <input
                data-testid="admin-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search..."
                className="pl-9 pr-4 py-2.5 rounded-full border border-neutral-200 bg-white text-sm w-full md:w-64 focus:outline-none focus:border-neutral-900"
              />
            </div>
            <button
              onClick={() => {
                const stamp = new Date().toISOString().slice(0, 10);
                if (tab === "applications") {
                  downloadCsv(`applications-${stamp}.csv`, filteredApps, APP_COLUMNS);
                } else {
                  downloadCsv(`contacts-${stamp}.csv`, filteredContacts, CONTACT_COLUMNS);
                }
              }}
              disabled={
                (tab === "applications" && !filteredApps.length) ||
                (tab === "contacts" && !filteredContacts.length)
              }
              data-testid="admin-csv-export"
              className="btn-pill btn-pill--secondary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" strokeWidth={1.75} />
              Export CSV
            </button>
            <button
              onClick={() => loadData()}
              className="btn-pill btn-pill--secondary"
              data-testid="admin-refresh"
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" strokeWidth={1.75} />}
              Refresh
            </button>
            <button
              onClick={handleLogout}
              className="btn-pill btn-pill--secondary"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.75} />
              Sign out
            </button>
          </div>
        </div>

        <div className="flex gap-1 mb-6 bg-white rounded-full border border-neutral-200 p-1 w-fit">
          <TabButton active={tab === "applications"} onClick={() => setTab("applications")} testid="tab-apps">
            <Users className="w-4 h-4" strokeWidth={1.75} />
            Applications ({applications.length})
          </TabButton>
          <TabButton active={tab === "contacts"} onClick={() => setTab("contacts")} testid="tab-contacts">
            <Mail className="w-4 h-4" strokeWidth={1.75} />
            Contact ({contacts.length})
          </TabButton>
        </div>

        {error && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 text-red-900 px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {tab === "applications" ? (
          <ApplicationsTable rows={filteredApps} />
        ) : (
          <ContactsTable rows={filteredContacts} />
        )}
      </div>
    </div>
  );
}

function filter(rows, q, fields) {
  if (!q) return rows;
  const needle = q.toLowerCase();
  return rows.filter((r) => fields.some((f) => (r[f] || "").toString().toLowerCase().includes(needle)));
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full transition-colors ${
        active ? "bg-neutral-950 text-white" : "text-neutral-700 hover:bg-neutral-100"
      }`}
    >
      {children}
    </button>
  );
}

function ApplicationsTable({ rows }) {
  if (!rows.length) return <EmptyState label="No applications yet." />;
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50 text-left text-xs uppercase tracking-[0.18em] text-neutral-500">
            <tr>
              <th className="px-6 py-4">Name</th>
              <th className="px-6 py-4">Contact</th>
              <th className="px-6 py-4">Domain</th>
              <th className="px-6 py-4">Qualification</th>
              <th className="px-6 py-4">College</th>
              <th className="px-6 py-4">Received</th>
            </tr>
          </thead>
          <tbody data-testid="admin-apps-tbody">
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-neutral-100 align-top">
                <td className="px-6 py-4 font-medium text-neutral-950">{r.full_name}</td>
                <td className="px-6 py-4 text-neutral-700">
                  <div>{r.email}</div>
                  <div className="text-xs text-neutral-500 mt-1">{r.phone}</div>
                </td>
                <td className="px-6 py-4 text-neutral-700">{r.domain || "—"}</td>
                <td className="px-6 py-4 text-neutral-700">{r.qualification}</td>
                <td className="px-6 py-4 text-neutral-700">{r.college || "—"}</td>
                <td className="px-6 py-4 text-neutral-500 text-xs font-mono whitespace-nowrap">
                  {formatDate(r.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ContactsTable({ rows }) {
  if (!rows.length) return <EmptyState label="No contact messages yet." />;
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50 text-left text-xs uppercase tracking-[0.18em] text-neutral-500">
            <tr>
              <th className="px-6 py-4">Name</th>
              <th className="px-6 py-4">Contact</th>
              <th className="px-6 py-4">Subject</th>
              <th className="px-6 py-4">Message</th>
              <th className="px-6 py-4">Received</th>
            </tr>
          </thead>
          <tbody data-testid="admin-ctc-tbody">
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-neutral-100 align-top">
                <td className="px-6 py-4 font-medium text-neutral-950">{r.full_name}</td>
                <td className="px-6 py-4 text-neutral-700">
                  <div>{r.email}</div>
                  <div className="text-xs text-neutral-500 mt-1">{r.phone || "—"}</div>
                </td>
                <td className="px-6 py-4 text-neutral-700">{r.subject || "—"}</td>
                <td className="px-6 py-4 text-neutral-700 max-w-md">
                  <span className="line-clamp-3">{r.message}</span>
                </td>
                <td className="px-6 py-4 text-neutral-500 text-xs font-mono whitespace-nowrap">
                  {formatDate(r.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 py-20 text-center text-neutral-500">
      {label}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function LoginView({ onLogin, loading, error }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  return (
    <div className="min-h-screen bg-neutral-50 pt-24 md:pt-28 pb-24">
      <div className="max-w-md mx-auto px-6">
        <div className="bg-white rounded-3xl border border-neutral-200 p-8 md:p-10 shadow-[0_20px_40px_rgba(0,0,0,0.04)]">
          <div className="w-12 h-12 rounded-full bg-neutral-950 text-white grid place-items-center mb-6">
            <Lock className="w-5 h-5" strokeWidth={1.75} />
          </div>
          <span className="text-xs font-mono uppercase tracking-[0.24em] text-neutral-500">Admin</span>
          <h1 className="mt-2 font-serif text-3xl tracking-tight">Sign in to dashboard</h1>
          <p className="mt-2 text-sm text-neutral-500">Restricted to Scontinent staff.</p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              onLogin(username, password);
            }}
            className="mt-8 space-y-5"
          >
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.18em] text-neutral-500 mb-1">Email</span>
              <input
                data-testid="admin-login-username"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="editorial-input"
                placeholder="office@scontinent.com"
              />
            </label>
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.18em] text-neutral-500 mb-1">Password</span>
              <input
                data-testid="admin-login-password"
                required
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="editorial-input"
                placeholder="••••••••"
              />
            </label>
            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 text-red-900 px-4 py-3 text-sm">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="btn-pill btn-pill--primary w-full"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.75} /> : <LogIn className="w-4 h-4" strokeWidth={1.75} />}
              Sign in
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
